import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { STATUS_FLOW, STATUS_COLORS } from "../../lib/constants";
import {
  ArrowLeft, Phone, Mail, MessageCircle, Calendar, AlertCircle,
  Clock, User, ChevronRight, MapPin, FileText,
} from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

function tageZwischen(a, b) {
  if (!a) return null;
  const ms = (b ? new Date(b) : new Date()) - new Date(a);
  return Math.max(0, Math.floor(ms / 86400000));
}

export default function VertrieblerEmpfehlungDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Status-Wechsel
  const [neuerStatus, setNeuerStatus] = useState("");
  const [auftragswert, setAuftragswert] = useState("");
  const [statusNotiz, setStatusNotiz] = useState("");
  const [statusError, setStatusError] = useState("");

  // Wiedervorlage
  const [wvDatum, setWvDatum] = useState("");
  const [wvNotiz, setWvNotiz] = useState("");
  const [wvError, setWvError] = useState("");
  const [wvSaving, setWvSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/empfehlungen/${id}`);
      setItem(data);
      setAuftragswert(data.auftragswert_netto || "");
      setWvDatum(data.wiedervorlage_am ? data.wiedervorlage_am.substring(0, 10) : "");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Empfehlung nicht gefunden.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const wechseln = async () => {
    setStatusError("");
    try {
      const payload = { neuer_status: neuerStatus, notiz: statusNotiz || undefined };
      if (neuerStatus === "Vertrag" || auftragswert) payload.auftragswert_netto = Number(auftragswert) || undefined;
      await api.post(`/empfehlungen/${id}/status`, payload);
      setNeuerStatus(""); setStatusNotiz("");
      load();
    } catch (e) {
      setStatusError(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Statuswechsel.");
    }
  };

  const speichernWiedervorlage = async () => {
    setWvError(""); setWvSaving(true);
    try {
      await api.post(`/empfehlungen/${id}/wiedervorlage`, {
        wiedervorlage_am: wvDatum || null,
        notiz: wvNotiz || undefined,
      });
      setWvNotiz("");
      load();
    } catch (e) {
      setWvError(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Speichern.");
    } finally {
      setWvSaving(false);
    }
  };

  if (loading) return <div className="text-falke-blue/60 text-sm" data-testid="detail-loading">Lade …</div>;
  if (error) return <div className="falke-card p-6" data-testid="detail-error">
    <div className="text-rose-600">{error}</div>
    <button onClick={() => navigate("/vertriebler")} className="btn-outline mt-3">Zurück zur Pipeline</button>
  </div>;
  if (!item) return null;

  const wvTage = tageZwischen(item.wiedervorlage_am, null);
  const wvUeberfaellig = item.wiedervorlage_am && new Date(item.wiedervorlage_am) < new Date(new Date().toDateString());

  return (
    <div className="space-y-5 max-w-5xl" data-testid="empfehlung-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/vertriebler" className="text-falke-blue/70 hover:text-falke-orange inline-flex items-center gap-1 text-sm" data-testid="back-to-pipeline">
          <ArrowLeft size={16} /> Pipeline
        </Link>
        <ChevronRight size={14} className="text-falke-blue/40" />
        <span className="text-sm text-falke-blue/60">Empfehlung</span>
      </div>

      {/* Header */}
      <div className="falke-card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-falke-blue" data-testid="detail-empfohlener-name">{item.empfohlener_name}</h1>
            <div className="text-sm text-falke-anthracite/70 mt-1">
              von <span className="font-semibold text-falke-blue">{item.empfehler_name}</span>
              {item.empfehler_modus === "gewerblich_aktiv" && (
                <span className="ml-2 inline-block px-1.5 py-0.5 bg-falke-orange/10 text-falke-orange rounded text-xs font-semibold">Gewerblich</span>
              )}
            </div>
          </div>
          <span className={`status-badge ${STATUS_COLORS[item.status]} text-sm`} data-testid="detail-current-status">{item.status}</span>
        </div>

        {/* Kontakt-Quick-Actions */}
        <div className="flex flex-wrap gap-2 mt-4">
          {item.empfohlener_telefon && (
            <a href={`tel:${item.empfohlener_telefon}`} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="qa-call">
              <Phone size={14} /> {item.empfohlener_telefon}
            </a>
          )}
          {item.empfohlener_email && (
            <a href={`mailto:${item.empfohlener_email}`} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="qa-mail">
              <Mail size={14} /> {item.empfohlener_email}
            </a>
          )}
          {item.empfohlener_telefon && (
            <a href={`https://wa.me/${item.empfohlener_telefon.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="qa-whatsapp">
              <MessageCircle size={14} /> WhatsApp
            </a>
          )}
        </div>

        {/* Eckdaten */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5 pt-5 border-t border-falke-blue/10">
          <div>
            <div className="text-xs text-falke-blue/60 uppercase tracking-wide mb-1">Erstellt</div>
            <div className="text-sm font-semibold text-falke-anthracite">{fmtDateTime(item.erstellt_am)}</div>
          </div>
          <div>
            <div className="text-xs text-falke-blue/60 uppercase tracking-wide mb-1">Im aktuellen Status seit</div>
            <div className="text-sm font-semibold text-falke-anthracite inline-flex items-center gap-1">
              {fmtDate(item.letzter_status_wechsel_am)}
              {tageZwischen(item.letzter_status_wechsel_am) > 7 && (
                <span className="text-rose-500 inline-flex items-center gap-0.5 text-xs ml-1"><AlertCircle size={12} /> {tageZwischen(item.letzter_status_wechsel_am)}d</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-falke-blue/60 uppercase tracking-wide mb-1">Auftragswert (netto)</div>
            <div className="text-sm font-semibold tabular text-falke-blue">{item.auftragswert_netto ? `€ ${Number(item.auftragswert_netto).toLocaleString("de-DE", { minimumFractionDigits: 2 })}` : "—"}</div>
          </div>
          <div>
            <div className="text-xs text-falke-blue/60 uppercase tracking-wide mb-1">Wiedervorlage</div>
            <div className={`text-sm font-semibold inline-flex items-center gap-1 ${wvUeberfaellig ? "text-rose-600" : "text-falke-anthracite"}`} data-testid="detail-wv-label">
              <Calendar size={13} /> {item.wiedervorlage_am ? fmtDate(item.wiedervorlage_am) : "—"}
              {wvUeberfaellig && <span className="text-xs">(überfällig)</span>}
              {!wvUeberfaellig && item.wiedervorlage_am && wvTage !== null && <span className="text-xs text-falke-blue/60">in {wvTage}d</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Status-Wechsel */}
        <div className="falke-card p-6 space-y-3" data-testid="status-change-card">
          <h3 className="font-heading text-lg text-falke-blue inline-flex items-center gap-2"><FileText size={18} /> Status ändern</h3>
          <label className="block text-sm font-medium text-falke-blue mb-1">Neuer Status</label>
          <select value={neuerStatus} onChange={(e) => setNeuerStatus(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="detail-status-select">
            <option value="">Auswählen …</option>
            {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {(neuerStatus === "Vertrag" || (auftragswert && neuerStatus)) && (
            <div>
              <label className="block text-sm font-medium text-falke-blue mb-1">Auftragswert (netto) *</label>
              <input type="number" step="0.01" value={auftragswert} onChange={(e) => setAuftragswert(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="detail-auftragswert" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1">Notiz (optional)</label>
            <textarea rows={2} value={statusNotiz} onChange={(e) => setStatusNotiz(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="detail-status-notiz" />
          </div>
          {statusError && <div className="text-sm text-rose-600" data-testid="detail-status-error">{statusError}</div>}
          <button onClick={wechseln} disabled={!neuerStatus} className="btn-primary w-full" data-testid="detail-status-submit">Status übernehmen</button>
        </div>

        {/* Wiedervorlage */}
        <div className="falke-card p-6 space-y-3" data-testid="wiedervorlage-card">
          <h3 className="font-heading text-lg text-falke-blue inline-flex items-center gap-2"><Calendar size={18} /> Wiedervorlage</h3>
          <p className="text-xs text-falke-anthracite/60">Erinnere dich an diesen Lead. Du siehst überfällige Wiedervorlagen rot markiert.</p>
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1">Datum</label>
            <input type="date" value={wvDatum} onChange={(e) => setWvDatum(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="wv-datum" />
          </div>
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1">Notiz (optional)</label>
            <textarea rows={2} value={wvNotiz} onChange={(e) => setWvNotiz(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="wv-notiz" placeholder="z. B. Termin nochmal anfragen" />
          </div>
          {wvError && <div className="text-sm text-rose-600" data-testid="wv-error">{wvError}</div>}
          <div className="flex gap-2">
            <button onClick={speichernWiedervorlage} disabled={wvSaving} className="btn-primary flex-1" data-testid="wv-save">{wvSaving ? "Speichern…" : "Speichern"}</button>
            {item.wiedervorlage_am && (
              <button onClick={() => { setWvDatum(""); speichernWiedervorlage(); }} disabled={wvSaving} className="btn-outline" data-testid="wv-clear">Entfernen</button>
            )}
          </div>
        </div>
      </div>

      {/* Empfehler-Karte */}
      <div className="falke-card p-6" data-testid="empfehler-card">
        <h3 className="font-heading text-lg text-falke-blue inline-flex items-center gap-2 mb-3"><User size={18} /> Empfehler</h3>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div><div className="text-xs text-falke-blue/60">Name</div><div className="font-semibold text-falke-anthracite">{item.empfehler_name}</div></div>
          {item.empfehler_email && <div><div className="text-xs text-falke-blue/60">E-Mail</div><a href={`mailto:${item.empfehler_email}`} className="text-falke-orange hover:underline">{item.empfehler_email}</a></div>}
          {item.empfehler_telefon && <div><div className="text-xs text-falke-blue/60">Telefon</div><a href={`tel:${item.empfehler_telefon}`} className="text-falke-orange hover:underline">{item.empfehler_telefon}</a></div>}
        </div>
      </div>

      {/* Bestehende Notizen */}
      {item.notiz && (
        <div className="falke-card p-6" data-testid="notiz-card">
          <h3 className="font-heading text-lg text-falke-blue inline-flex items-center gap-2 mb-3"><MapPin size={18} /> Notizen</h3>
          <pre className="whitespace-pre-wrap text-sm text-falke-anthracite font-sans">{item.notiz}</pre>
        </div>
      )}

      {/* Statushistorie */}
      <div className="falke-card p-6" data-testid="historie-card">
        <h3 className="font-heading text-lg text-falke-blue inline-flex items-center gap-2 mb-4"><Clock size={18} /> Status-Verlauf ({(item.historie || []).length})</h3>
        {(item.historie || []).length === 0 ? (
          <div className="text-sm text-falke-anthracite/60">Noch keine Statuswechsel.</div>
        ) : (
          <ol className="relative border-l-2 border-falke-blue/15 ml-3 space-y-5">
            {item.historie.map((h, i) => (
              <li key={h.id || i} className="ml-4" data-testid={`historie-item-${i}`}>
                <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-falke-orange border-2 border-white" />
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className={`status-badge ${STATUS_COLORS[h.von_status] || ""} text-xs`}>{h.von_status || "—"}</span>
                  <ChevronRight size={12} className="text-falke-blue/40" />
                  <span className={`status-badge ${STATUS_COLORS[h.zu_status] || ""} text-xs`}>{h.zu_status}</span>
                </div>
                <div className="text-xs text-falke-anthracite/60 mt-1">
                  {fmtDateTime(h.zeitstempel)} · durch <span className="font-semibold text-falke-blue/80">{h.durch_user_name || "System"}</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
